import { FuelUnit } from '@fe/core';
import { Slide } from '@progress/kendo-react-animation';
import { Button, DropDownButton, DropDownButtonItemClickEvent, SplitButton, SplitButtonItem } from '@progress/kendo-react-buttons';
import { SVGIcon, SvgIcon } from '@progress/kendo-react-common';
import { ToggleButton } from '@progress/kendo-react-dateinputs';
import { Checkbox } from '@progress/kendo-react-inputs';
import { clipboardTextIcon } from '@progress/kendo-svg-icons';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React, { CSSProperties, useCallback, useEffect } from 'react';
import { OpenInMultiWindowButton } from '../../app/components/multi-window/OpenMultiWindowButton';
import { useMultiWindowContext } from '../../app/components/multi-window/context/multi-window-context';
import { WorkspaceWindowPanelType } from '../../app/components/multi-window/types';
import { useEvent } from '../../app/contexts/event-bus';
import { PanelTitleLeadingContent, PanelTitleTrailingContent } from '../../app/features/home/panels/HomePanel';
import { useAppDispatch, useAppSelector } from '../../app/hooks/store-hooks';
import { useFlightWorker } from '../../app/hooks/use-flight-worker';
import { addNotification, NotificationType } from '../../app/state/slices/global-slice';
import {
    NewRouteModel,
    remoteAnalyzerRemoveAllRoutes,
    routeAnalyzerAddRoutes,
    routeAnalyzerRemoveRoute,
    routeAnalyzerSetAllRoutesVisible,
    routeAnalyzerUpdateRoute,
    routeAnalyzerUpdateSetting
} from '../../app/state/slices/route-analyzer-slice';
import { setSetting } from '../../app/state/slices/settings-slice';
import { setWorkspacePanelVisibility } from '../../app/state/slices/workspace-slice';
import { etopsApi2DataModel } from '../../flights/interop/flightDto';
import { DateTimeFormatter } from '../../formatters';
import commonFormatter from '../../formatters/common-formatter';
import useIntegration from '../../hooks/use-integration';
import useMapInteraction from '../../hooks/use-map-interaction';
import {
    AddOutline,
    AddTextOutline,
    CloseOutline,
    DatabaseOutline,
    DataInsightsOutline,
    DownloadOutline,
    DuplicateOutline,
    EditOutline,
    ExternalLinkOutline,
    FavoriteOutline,
    FavoriteSolid,
    FeedbackOutline,
    FileExportOutline,
    InfoCircleOutline,
    ShareOutline,
    TrashOutline
} from '../../icons/icons';
import { FeServerEtopsModel, FeServerRouteModel, FeServerRouteWaypointModel } from '../../messages/message-json';
import { MessageType } from '../../services/actor-messages';
import { FeRouteApiService } from '../../services/axios/axios-api-generated';
import { FpmRouteAction, notifyFpmRequestNotPossible, sendRouteToFpm } from '../../services/integration/fpm-request-tracker';
import useRouteAnalyzerLayer from '../hooks/useRouteAnalyzerLayer';
import EditRoute from './EditRoute';
import { DatabaseImportRoutes } from './import/DatabaseImportRoutes';
import { FileImportRoutes } from './import/FileImportRoutes';
import { FpmImportRoutes } from './import/FpmImportRoutes';
import { RouteAnalyzerLayer } from './layers/route-analyzer-layer';
import {
    AnalyzerRoute,
    AnalyzerRouteAssociatedPoint,
    AnalyzerRouteType,
    MAX_VISIBLE_ROUTE_COUNT,
    MinimalAnalyzerRoute,
    RouteTypeToName
} from './types';

export function RouteAnalyzer(props: { className?: string; style?: CSSProperties }) {
    const isVisible = useAppSelector((x) => x.workspace.panels.routeAnalyzer);
    const routeAnalyzerVisibleInExternalWindow = useAppSelector((x) =>
        x.workspace.windows.some((x) => x.panels.some((x) => x.type === WorkspaceWindowPanelType.routeAnalyzer))
    );
    const multiWindowContext = useMultiWindowContext();
    const previouslyVisibleRoutes = React.useRef<AnalyzerRoute[]>([]);
    const previousAnalyzerLayer = React.useRef<RouteAnalyzerLayer | null>(null);
    const routesLayer = useRouteAnalyzerLayer();
    const mapInteraction = useMapInteraction();
    const [addVisible, setAddVisible] = React.useState(false);
    const routes = useAppSelector((x) => x.routeAnalyzer.routes);
    const [editItem, setEditItem] = React.useState<AnalyzerRoute | undefined>(undefined);
    const [showImportRoutePopup, setShowImportRoutesPopup] = React.useState(false);
    const [showFPMImportRoutes, setShowFPMImportRoutes] = React.useState(false);
    const [showDatabaseImportRoutes, setShowDatabaseImportRoutes] = React.useState(false);
    const drawLabels = useAppSelector((x) => x.routeAnalyzer.settings.drawLabels);
    const drawWaypoints = useAppSelector((x) => x.routeAnalyzer.settings.drawWaypoints);
    const favoriteAddIdx = useAppSelector((state) => state.settings.favoriteAddRouteMethod);
    const [isEditing, setIsEditing] = React.useState(false);
    const integrationInfo = useIntegration();
    const integrationImportDisabledReason = integrationInfo.enabled ? integrationInfo.getDisabledReason('canRequestRouteFromFPM') : null;
    const [showInfoPopup, setShowInfoPopup] = React.useState(false);
    const [showMaxVisibleCountError, setShowMaxVisibleCountError] = React.useState(false);
    const shouldShowInfoPopup = !useAppSelector((state) => state.settings.graphicRouteEditInfoPopupDismissed);
    const visibleRoutesCount = routes.reduce((count, next) => (next.visible ? count + 1 : count), 0);
    const maxVisibleCountReached = visibleRoutesCount >= MAX_VISIBLE_ROUTE_COUNT;
    const hasRouteAnalyzerInExternalWindow = useAppSelector((x) =>
        x.workspace.windows.some((x) => x.panels.some((x) => x.type === WorkspaceWindowPanelType.routeAnalyzer))
    );
    const dispatch = useAppDispatch();
    const worker = useFlightWorker();

    React.useEffect(() => {
        const previousLayer = previousAnalyzerLayer.current;
        if (previousLayer) {
            // Copy routes from previous layer
            previousLayer.removeAll();
            previouslyVisibleRoutes.current = [];
        }

        previousAnalyzerLayer.current = routesLayer;
    }, [routesLayer]);

    React.useEffect(() => {
        for (const route of previouslyVisibleRoutes.current) {
            if (!routes.includes(route)) {
                routesLayer?.remove(route);
            }
        }

        for (const route of routes) {
            if (!route.visible) {
                continue;
            }

            if (!previouslyVisibleRoutes.current.includes(route)) {
                if (route.waypoints) {
                    routesLayer?.add(route);
                }
            }
        }
        previouslyVisibleRoutes.current = routes.filter((x) => x.visible);
    }, [routes, routesLayer]);

    // If the layers (focused workspace) changed, set edit to false.
    // Here we could copy edit state but there's no time.
    useEffect(() => {
        setIsEditing(false);
    }, [routesLayer]);

    React.useEffect(() => {
        if (isEditing) {
            mapInteraction.setTooltips('disabled');
            mapInteraction.setContextMenu('disabled');
        } else {
            mapInteraction.setTooltips('enabled');
            mapInteraction.setContextMenu('enabled');
        }
    }, [mapInteraction, isEditing]);

    React.useEffect(() => {
        routesLayer?.setLabels(drawLabels);
    }, [routesLayer, drawLabels]);

    React.useEffect(() => {
        routesLayer?.setWaypoints(drawWaypoints);
    }, [routesLayer, drawWaypoints]);

    useEvent(
        'route-analyzer-edit-save',
        useCallback((e) => {
            setIsEditing(false);
            dispatch(routeAnalyzerUpdateRoute({ route: e.route }));
        }, [])
    );

    useEvent(
        'route-analyzer-edit-cancel',
        useCallback(() => {
            setIsEditing(false);
        }, [])
    );
    useEvent(
        'route-analyzer-load-flight',
        React.useCallback(
            async (flightId: number) => {
                // if we're in the main window
                if (!multiWindowContext.active) {
                    // if there's a route analyzer already open in a tear off window,
                    if (hasRouteAnalyzerInExternalWindow) {
                        // ignore
                        return;
                    }
                    dispatch(setWorkspacePanelVisibility({ panel: 'routeAnalyzer', visible: true }));
                }
                // else we're in a tear off window

                dispatch(remoteAnalyzerRemoveAllRoutes());
                worker.actor
                    .sendAsync({
                        type: MessageType.getFlightPlanHistory,
                        data: flightId
                    })
                    .then((response) => {
                        if (!response) {
                            return;
                        }

                        dispatch(
                            routeAnalyzerAddRoutes({
                                routes: response.routes.map((item, i) =>
                                    newRoute({
                                        type: AnalyzerRouteType.flight,
                                        label: item.id?.trim() || response.acid + '-' + i,
                                        origin: item.wp[0].id,
                                        destination: item.wp[item.wp.length - 1].id,
                                        sequence: item.st ? RouteStatusToString[item.st] : '',
                                        time: dayjs(item.ts).unix(),
                                        route: item.rtxt,
                                        waypoints: {
                                            items: item.wp,
                                            fuelUnit: item.fu as unknown as FuelUnit,
                                            altitudeUnits: item.au
                                        },
                                        associatedPoints: etopsToAssociatedPoints(item.etops),
                                        feedId: item.fd,
                                        flightId
                                    })
                                )
                            })
                        );
                    });
            },
            [worker, dispatch, multiWindowContext, hasRouteAnalyzerInExternalWindow]
        )
    );

    useEvent(
        'route-analyzer-load-routes',
        React.useCallback(
            (routes: MinimalAnalyzerRoute[]) => {
                dispatch(setWorkspacePanelVisibility({ panel: 'routeAnalyzer', visible: true }));
                const newRoutes = routes.map((route) =>
                    newRoute({
                        ...route,
                        sequence: route.sequence || '',
                        label: route.label || '',
                        type: AnalyzerRouteType.adhoc,
                        distance: route.distance ?? null,
                        //color: ROUTE_ANALYZER_DEFAULT_VERIFY_COLOR, Does this matter?
                        visible: true
                    })
                );
                dispatch(routeAnalyzerAddRoutes({ routes: newRoutes, replace: true }));
            },
            [dispatch]
        )
    );

    const handleRemoveAll = React.useCallback(() => {
        dispatch(remoteAnalyzerRemoveAllRoutes());
    }, []);

    const handleDelete = (id: string) => {
        dispatch(routeAnalyzerRemoveRoute({ id }));
    };

    const handleEdit = (item: AnalyzerRoute) => {
        setEditItem(item);
        setAddVisible(true);
    };

    const handleDuplicateRoute = (item: AnalyzerRoute) => {
        const idx = routes.indexOf(item);
        if (idx < 0) {
            return;
        }
        dispatch(routeAnalyzerAddRoutes({ routes: [{ ...item, label: item.label + ' copy' }], beforeId: item.id }));
    };

    const handleCopyRouteToClipboard = React.useCallback(async (route: AnalyzerRoute) => {
        await navigator.clipboard.writeText(route.route);
    }, []);

    const handleCalcOrVerify = React.useCallback((route: AnalyzerRoute, actionInfo: DropDownButtonItemClickEvent) => {
        sendRouteToFpm((actionInfo.item as RouteFpmActionItem).action, route);
    }, []);

    const toggleVisibleRoute = (item: AnalyzerRoute) => {
        if (item.visible) {
            dispatch(
                routeAnalyzerUpdateRoute({
                    route: {
                        id: item.id,
                        visible: false
                    }
                })
            );
        } else {
            if (item.waypoints) {
                dispatch(
                    routeAnalyzerUpdateRoute({
                        route: {
                            id: item.id,
                            visible: true
                        }
                    })
                );
            } else {
                FeRouteApiService.decodeAdhocRoute(
                    {
                        body: {
                            routes: [item.route]
                        }
                    },
                    {
                        withAuthorization: true
                    }
                )
                    .then((response) => {
                        if (response.responseCode || !response.routes?.[0].wp) {
                            dispatch(addNotification({ type: NotificationType.routeDecodeError }));
                            return;
                        }
                        dispatch(
                            routeAnalyzerUpdateRoute({
                                route: {
                                    id: item.id,
                                    visible: true,
                                    waypoints: {
                                        items: response.routes[0].wp as FeServerRouteWaypointModel[]
                                    }
                                }
                            })
                        );
                    })
                    .catch(() => dispatch(addNotification({ type: NotificationType.routeDecodeError })));
            }
        }
    };

    const handleCheckAll = (value: boolean) => {
        if (value) {
            if (routes.length > MAX_VISIBLE_ROUTE_COUNT) {
                setShowMaxVisibleCountError(true);
                return;
            }
            dispatch(routeAnalyzerSetAllRoutesVisible(true));
        } else {
            dispatch(routeAnalyzerSetAllRoutesVisible(false));
        }
    };

    const handleAddClick = (idx: number) => {
        if (idx === 0) {
            setAddVisible(true);
        } else if (idx === 1) {
            setShowDatabaseImportRoutes(true);
        } else if (idx === 2) {
            setShowImportRoutesPopup(true);
        } else if (idx === 3) {
            // can be triggered through the favorite shortcut even when the item is disabled
            if (integrationImportDisabledReason) {
                notifyFpmRequestNotPossible(`Cannot import from ${integrationInfo.typeText}: ${integrationImportDisabledReason}`);
                return;
            }
            setShowFPMImportRoutes(true);
        }
    };

    const handleEditPopupClose = () => {
        setAddVisible(false);
        setEditItem(undefined);
    };

    const handleExportRoutes = async () => {
        await exportRoutes(routes);
    };

    useEvent(
        'route-analyzer-edit-start',
        React.useCallback(
            ({ id }) => {
                const route = routes.find((x) => x.id === id);
                if (!route) {
                    return;
                }

                if (shouldShowInfoPopup) {
                    setShowInfoPopup(true);
                }

                setIsEditing(true);
                routesLayer?.edit(route);
            },
            [routesLayer, routes, shouldShowInfoPopup]
        )
    );

    const handleUpdateFavoriteItemIdx = React.useCallback((idx: number) => {
        dispatch(setSetting('favoriteAddRouteMethod', idx));
    }, []);

    const renderSplitButtonItem = React.useCallback(
        (props: { itemIndex: number; item: { text: string; disabled?: boolean; svgIcon?: SVGIcon } }) => {
            return (
                <div className="d-flex gap-2 w-100 align-items-center" id={`add-route-btn-${props.itemIndex}`}>
                    <SvgIcon icon={indexToIcon[props.itemIndex]}></SvgIcon>
                    {props.itemIndex === 3 && integrationImportDisabledReason ? (
                        <span className="flex-grow-1 d-flex flex-column" title={integrationImportDisabledReason}>
                            <span>{props.item.text}</span>
                            <span className="secondary-text" style={{ fontSize: 11, whiteSpace: 'normal', maxWidth: 220 }}>
                                {integrationImportDisabledReason}
                            </span>
                        </span>
                    ) : (
                        <span className="flex-grow-1">{props.item.text}</span>
                    )}
                    <Button
                        svgIcon={favoriteAddIdx === props.itemIndex ? FavoriteSolid : FavoriteOutline}
                        fillMode={'flat'}
                        className="button-small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateFavoriteItemIdx(props.itemIndex);
                        }}></Button>
                </div>
            );
        },
        [favoriteAddIdx, integrationImportDisabledReason]
    );
    const handleDismissInfoPopup = React.useCallback(() => {
        if (shouldShowInfoPopup) {
            dispatch(setSetting('graphicRouteEditInfoPopupDismissed', true));
        }
        setShowInfoPopup(false);
    }, []);

    const handleCloseInfoPopup = React.useCallback(() => {
        setShowInfoPopup(false);
    }, []);

    if (multiWindowContext.active) {
        // External window
    } else {
        // Main window
        if (!isVisible || routeAnalyzerVisibleInExternalWindow) {
            return <></>;
        }
    }

    return (
        <div
            className={clsx(props.className, 'position-relative')}
            style={{ ...props.style, backgroundColor: 'light-dark(white, var(--color-neutral-050))' }}>
            <Slide
                componentChildClassName="p-2"
                direction="left"
                style={{ position: 'absolute', right: '0px', bottom: '0px', width: '275px', top: 'unset', left: 'unset', zIndex: 10 }}>
                {showInfoPopup && (
                    <div className="k-card">
                        <div className="position-relative px-3 py-3 pb-2">
                            <SvgIcon themeColor="primary" icon={InfoCircleOutline} className="icon-b-2" />
                            <span className="ps-1">Graphical route edit</span>
                            <ul className="m-0" style={{ paddingLeft: '21px' }}>
                                <li>
                                    <div className="secondary-text pt-1">
                                        To save your changes press <strong>Enter</strong>.
                                    </div>
                                </li>
                                <li>
                                    <div className="secondary-text">
                                        To discard your changes press <strong>Escape</strong>.
                                    </div>
                                </li>
                                <li>
                                    <div className="secondary-text">
                                        Route by default snaps to any visible fix or navaid. To place the route waypoint anywhere press{' '}
                                        <strong>ALT</strong>.
                                    </div>
                                </li>
                            </ul>
                            <Button
                                svgIcon={CloseOutline}
                                className="button-small"
                                fillMode={'flat'}
                                style={{ position: 'absolute', top: 5, right: 5 }}
                                onClick={handleCloseInfoPopup}></Button>
                            <div className="pt-1 d-flex justify-content-center">
                                <Button
                                    fillMode="outline"
                                    style={{ fontSize: '.75rem' }}
                                    className="w-100"
                                    onClick={handleDismissInfoPopup}>
                                    <SvgIcon icon={FeedbackOutline} />
                                    <span className="ps-1">Don't show again</span>
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Slide>

            <Slide
                componentChildClassName="p-2"
                direction="left"
                style={{ position: 'absolute', right: '0px', bottom: '0px', top: 'unset', left: 'unset', zIndex: 10 }}>
                {showMaxVisibleCountError && (
                    <div className="k-card bg-warning p-2">
                        <div className="d-flex gap-1">
                            <div>Only {MAX_VISIBLE_ROUTE_COUNT} routes can be visible at a time</div>
                            <Button
                                svgIcon={CloseOutline}
                                fillMode={'flat'}
                                className="button-small"
                                onClick={() => setShowMaxVisibleCountError(false)}
                            />
                        </div>
                    </div>
                )}
            </Slide>

            <div className="h-100 d-flex flex-column" id="route-analyzer">
                {addVisible && <EditRoute editItem={editItem} onClose={handleEditPopupClose} />}

                {/* Imports */}
                {showImportRoutePopup && <FileImportRoutes onClose={() => setShowImportRoutesPopup(false)} />}
                {showFPMImportRoutes && <FpmImportRoutes onClose={() => setShowFPMImportRoutes(false)} />}
                {showDatabaseImportRoutes && <DatabaseImportRoutes onClose={() => setShowDatabaseImportRoutes(false)} />}

                <PanelTitleLeadingContent>
                    <div className="position-relative">
                        <SplitButton
                            id="add-route-btn"
                            svgIcon={AddOutline}
                            fillMode={'flat'}
                            onButtonClick={() => handleAddClick(favoriteAddIdx ?? 0)}
                            onItemClick={(e) => handleAddClick(e.itemIndex)}
                            popupSettings={{ popupClass: 'k-menu-link-me-0' }}
                            itemRender={renderSplitButtonItem}>
                            <SplitButtonItem text="Ad-hoc" />
                            <SplitButtonItem text="Database" />
                            <SplitButtonItem text="Import from CSV" />
                            {integrationInfo.enabled && (
                                <SplitButtonItem text={integrationInfo.typeText} disabled={!!integrationImportDisabledReason} />
                            )}
                        </SplitButton>

                        {favoriteAddIdx != null && (
                            <SvgIcon style={{ position: 'absolute', right: 20, bottom: 0, width: 13 }} icon={indexToIcon[favoriteAddIdx]} />
                        )}
                    </div>
                </PanelTitleLeadingContent>
                <PanelTitleTrailingContent>
                    <div className="d-flex route-analyzer-button-group">
                        <ToggleButton
                            className="button-small rounded-0"
                            title="Show waypoints"
                            selected={drawWaypoints}
                            togglable={true}
                            fillMode={'flat'}
                            onClick={() => dispatch(routeAnalyzerUpdateSetting({ key: 'drawWaypoints', value: !drawWaypoints }))}
                            svgIcon={ShareOutline}
                        />
                        <ToggleButton
                            className="button-small rounded-0"
                            title="Show labels"
                            selected={drawLabels}
                            onClick={() => dispatch(routeAnalyzerUpdateSetting({ key: 'drawLabels', value: !drawLabels }))}
                            togglable={true}
                            fillMode={'flat'}
                            svgIcon={AddTextOutline}
                        />
                        <Button
                            className="button-small"
                            fillMode={'flat'}
                            title="Export"
                            svgIcon={FileExportOutline}
                            onClick={handleExportRoutes}
                        />

                        <Button
                            className="button-small"
                            fillMode={'flat'}
                            title="Clear all"
                            svgIcon={TrashOutline}
                            onClick={handleRemoveAll}
                        />

                        <div className="border-right">
                            <OpenInMultiWindowButton type={WorkspaceWindowPanelType.routeAnalyzer} />
                        </div>

                        {!multiWindowContext.active && (
                            <Button
                                className="button-small"
                                fillMode={'flat'}
                                title="Close route analyzer"
                                svgIcon={CloseOutline}
                                onClick={() => dispatch(setWorkspacePanelVisibility({ panel: 'routeAnalyzer', visible: false }))}
                            />
                        )}
                    </div>
                </PanelTitleTrailingContent>

                <div className="h-100 p-0 overflow-auto">
                    <table className="cobalt-table cobalt-alternate cobalt-compact w-100" id="route-analyzer-list">
                        <thead
                            style={{
                                position: 'sticky',
                                top: -1,
                                zIndex: 2,
                                height: 30,
                                backgroundColor: 'var(--alternate-color)'
                            }}>
                            <tr style={{ height: '30px' }}>
                                <th className="ps-2">
                                    <Checkbox
                                        value={visibleRoutesCount === routes.length ? true : visibleRoutesCount === 0 ? false : null}
                                        onChange={(e) => handleCheckAll(e.value)}
                                    />
                                </th>
                                <th>Type</th>
                                <th>Label</th>
                                <th>Origin</th>
                                <th>Dest</th>
                                <th>Seq</th>
                                <th>Dist</th>
                                <th>ETE</th>
                                <th>Time</th>
                                <th>Feed</th>
                                <th style={{ width: '50px' }}>Color</th>
                                <th>Route</th>
                                <th style={{ position: 'sticky', right: 0 }} className="text-end">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {routes.map((item, i) => (
                                <tr key={item.id}>
                                    <td className="ps-2 pt-1">
                                        <Checkbox
                                            disabled={maxVisibleCountReached && !item.visible}
                                            className="route-checkbox"
                                            value={item.visible}
                                            onChange={() => toggleVisibleRoute(item)}
                                        />
                                    </td>
                                    <td className="text-nowrap">{RouteTypeToName[item.type]}</td>
                                    <td className="text-nowrap">{item.label}</td>
                                    <td className="text-nowrap">{item.origin}</td>
                                    <td className="text-nowrap">{item.destination}</td>
                                    <td className="text-nowrap">{item.sequence || '-'}</td>
                                    <td className="text-nowrap">{item.distance ?? '-'}</td>
                                    <td className="text-nowrap">{item.ete || '-'}</td>
                                    <td className="text-nowrap">{DateTimeFormatter.formatTime(item.time) || '-'}</td>
                                    <td className="text-nowrap">{commonFormatter.formatFeedId(item.feedId) || '-'}</td>
                                    <td className="text-nowrap">
                                        <div
                                            className="mx-auto"
                                            style={{
                                                height: '20px',
                                                aspectRatio: '1/1',
                                                backgroundColor: item.color,
                                                border: '1px solid rgba(0,0,0,0.33)'
                                            }}></div>
                                    </td>
                                    <td className="text-nowrap">{item.route}</td>
                                    <td
                                        className={i % 2 ? 'alternate-bg-color' : 'primary-bg-color'}
                                        style={{ position: 'sticky', right: 0 }}>
                                        <div className="d-flex gap-2 ps-2 text-nowrap justify-content-end route-action-section">
                                            {integrationInfo.enabled && <RouteFpmActions route={item} onItemClick={handleCalcOrVerify} />}
                                            <Button
                                                style={{ width: 23, height: 23 }}
                                                svgIcon={clipboardTextIcon}
                                                title="Copy route"
                                                fillMode={'outline'}
                                                onClick={() => handleCopyRouteToClipboard(item)}
                                            />
                                            <Button
                                                style={{ width: 23, height: 23 }}
                                                svgIcon={DuplicateOutline}
                                                title="Duplicate route"
                                                fillMode={'outline'}
                                                onClick={() => handleDuplicateRoute(item)}
                                            />
                                            <Button
                                                style={{ width: 23, height: 23 }}
                                                svgIcon={EditOutline}
                                                title="Edit route"
                                                fillMode={'outline'}
                                                onClick={() => handleEdit(item)}
                                            />
                                            <Button
                                                style={{ width: 23, height: 23 }}
                                                svgIcon={TrashOutline}
                                                title="Delete route"
                                                fillMode={'outline'}
                                                onClick={() => handleDelete(item.id)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {routes.length === 0 && (
                                <tr>
                                    <td colSpan={99}>
                                        {routes.length === 0 && <div className="p-2 text-center">No routes available.</div>}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const indexToIcon: Record<number, SVGIcon> = {
    0: DataInsightsOutline,
    1: DatabaseOutline,
    2: DownloadOutline,
    3: ExternalLinkOutline
};

async function exportRoutes(routes: AnalyzerRoute[]) {
    if (!window.showSaveFilePicker) {
        return;
    }

    const result = await window.showSaveFilePicker({
        suggestedName: 'routes.csv',
        types: [
            {
                accept: {
                    'text/plain': ['.csv', '.txt']
                }
            }
        ]
    });

    const writable = await result.createWritable();
    for (const route of routes) {
        writable.write(`${route.type},${route.origin},${route.destination},${route.sequence},${route.route},${route.label}\n`);
    }

    writable.close();
}

const RouteStatusToString: Record<NonNullable<FeServerRouteModel['st']>, string> = {
    S: 'Schd',
    P: 'Plnd',
    A: 'Actv'
};

function etopsToAssociatedPoints(feServerEtops: FeServerEtopsModel | null): AnalyzerRouteAssociatedPoint[] {
    const etops = etopsApi2DataModel(feServerEtops);
    return (
        etops?.airports.map((x) => ({
            enabled: true,
            label: x.icaoId ?? x.iataId,
            longitude: x.position[0],
            latitude: x.position[1],
            range: etops.ruleDistance ?? 0,
            color: 'rgba(128, 128, 128, 0.05)'
        })) ?? []
    );
}

function newRoute(patch: Partial<NewRouteModel>): NewRouteModel {
    return {
        type: AnalyzerRouteType.adhoc,
        label: '',
        origin: '',
        destination: '',
        sequence: '',
        distance: null,
        ete: null,
        time: null,
        route: '',
        waypoints: null,
        associatedPoints: [],
        feedId: null,
        visible: false,
        ...patch
    };
}

interface RouteFpmActionItem {
    text: string;
    action: FpmRouteAction;
    disabled: boolean;
}

function RouteFpmActions(props: {
    route: AnalyzerRoute;
    onItemClick: (route: AnalyzerRoute, event: DropDownButtonItemClickEvent) => void;
}) {
    const integrationInfo = useIntegration();
    const routeReason = props.route.flightId === undefined ? 'route is not linked to a flight' : null;
    const calculateReason = integrationInfo.getDisabledReason('canCalcRouteInFPM') ?? routeReason;
    const verifyReason = integrationInfo.getDisabledReason('canVerifyRouteInFPM') ?? routeReason;

    const items: RouteFpmActionItem[] = [
        { text: `Calculate route in ${integrationInfo.typeText}`, action: 'calculate', disabled: !!calculateReason },
        { text: `Verify route in ${integrationInfo.typeText}`, action: 'verify', disabled: !!verifyReason }
    ];

    const title = [
        `Send to ${integrationInfo.typeText}`,
        calculateReason && `Calculate unavailable: ${calculateReason}`,
        verifyReason && `Verify unavailable: ${verifyReason}`
    ]
        .filter(Boolean)
        .join('\n');

    return (
        <DropDownButton
            style={{ width: 23, height: 23 }}
            svgIcon={ExternalLinkOutline}
            disabled={!!calculateReason && !!verifyReason}
            items={items}
            textField="text"
            title={title}
            onItemClick={(e) => props.onItemClick(props.route, e)}
        />
    );
}
